import React, { useState, useEffect } from "react";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import { ReactComponent as LinkedinIcon } from "../../../assets/svg/linkedInLogoSvg.svg";
import { ReactComponent as ClassicLinkedinIcon } from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/svg/linkedIn.svg";
import { ReactComponent as LinkedinPremiumIcon } from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/svg/liPremiumIcon.svg";
import CustomRadioButton from "../common/customRadioBtn";
import Button from "../common/Button";
import userDetail from "Stores/userDetail";
import linkedInUtils from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/common/linkedInUtils";
import { pluginNotInstalled } from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/popup/PluginNotInstalled";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "../common/toaster";

class AddLinkedInAccountPopupState {
  @observable showPopup = false;
  @observable cbk = () => {};
  @observable message = "";

  @action
  setShowPopup = (value = false) => {
    this.showPopup = value;
  };
}

const addLinkedInAccountPopupState = new AddLinkedInAccountPopupState();

const AddLinkedInAccountPopup = observer(() => {
  const [view, setView] = useState("chooseType");
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [connectMethod, setConnectMethod] = useState("oauth");
  const [selectedAccountType, setSelectedAccountType] =
    useState("salesNavigator");

  // Re-derive the default choice every time the popup opens, since
  // extension detection can resolve after this component's first mount.
  useEffect(() => {
    if (addLinkedInAccountPopupState.showPopup) {
      setConnectMethod(
        userDetail?.linkedInConnectpluginInstalled ? "extension" : "oauth"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLinkedInAccountPopupState.showPopup]);

  if (!addLinkedInAccountPopupState?.showPopup) {
    return null;
  }

  const closePopup = () => {
    addLinkedInAccountPopupState.setShowPopup(false);
    setView("chooseType");
    setInviteEmail("");
    setSendingInvite(false);
    setSelectedAccountType("salesNavigator");
  };

  const handleContinue = () => {
    if (connectMethod === "extension") {
      setView("chooseExtensionType");
    } else {
      linkedInUtils.connectViaOAuth();
      closePopup();
    }
  };

  const handleAccountTypeRadioChange = (e, id) => {
    setSelectedAccountType(id);
  };

  const handleContinueExtensionType = () => {
    linkedInUtils.connectViaExtension(selectedAccountType !== "standard");
    closePopup();
  };

  const handleSendInvite = async () => {
    if (!inviteEmail?.trim() || sendingInvite) {
      return;
    }
    setSendingInvite(true);
    const config = {
      url: URLS.sendLinkedInConnectInvite,
      method: "POST",
      data: { toAddress: inviteEmail.trim() }
    };
    const response = await makeApi(config);
    setSendingInvite(false);
    if (response?.data?.status) {
      setView("inviteSent");
    } else {
      toasterState.setToastMsg(
        "Something went wrong while sending the invite. Please try again.",
        "fail"
      );
    }
  };

  const extensionDetected = !!userDetail?.linkedInConnectpluginInstalled;

  const renderChooseType = () => (
    <>
      <div className="commonPopupHeader linkedInAccountPopupHeader">
        <div className="linkedinIcon">
          <LinkedinIcon />
        </div>
        <div className="linkedinHeadingWrapper">
          <div className="headingTitle">Connect LinkedIn</div>
          <div className="chooseAccountType">
            Choose how you'd like to connect your account
          </div>
        </div>
      </div>
      <div className="commonPopupBody linkedInAccountPopupBody">
        <div className="linkedInAccountOptions">
          <div
            className={`linkedInAccountOption${
              connectMethod === "extension" ? " selected" : ""
            }`}
            onClick={() => setConnectMethod("extension")}
          >
            <div className="radioBtnCont">
              <div className="radioButtonCont">
                <input
                  type="radio"
                  name="linkedInConnectMethod"
                  id="connectMethodExtension"
                  className="radioBtn"
                  checked={connectMethod === "extension"}
                  onChange={() => setConnectMethod("extension")}
                />
                <span className="contentClass" />
                <label htmlFor="connectMethodExtension" />
              </div>
            </div>
            <div className="optionContent">
              <div className="optionTitle">
                Quick connect with the extension
                {extensionDetected && (
                  <span className="recommended"> &middot; Recommended</span>
                )}
              </div>
              {extensionDetected ? (
                <div className="optionDescription">
                  Requires the Salesgear extension &mdash; connects instantly
                  from your logged-in LinkedIn session.
                </div>
              ) : (
                <div className="optionDescription">
                  Needs the Salesgear browser extension.{" "}
                  <span
                    className="installExtensionLink"
                    onClick={e => {
                      e.stopPropagation();
                      pluginNotInstalled();
                    }}
                  >
                    Install the extension
                  </span>{" "}
                  to connect instantly using your logged-in LinkedIn session.
                </div>
              )}
            </div>
          </div>

          <div
            className={`linkedInAccountOption${
              connectMethod === "oauth" ? " selected" : ""
            }`}
            onClick={() => setConnectMethod("oauth")}
          >
            <div className="radioBtnCont">
              <div className="radioButtonCont">
                <input
                  type="radio"
                  name="linkedInConnectMethod"
                  id="connectMethodOauth"
                  className="radioBtn"
                  checked={connectMethod === "oauth"}
                  onChange={() => setConnectMethod("oauth")}
                />
                <span className="contentClass" />
                <label htmlFor="connectMethodOauth" />
              </div>
            </div>
            <div className="optionContent">
              <div className="optionTitle">
                Connect via LinkedIn login
                {!extensionDetected && (
                  <span className="recommended"> &middot; Recommended</span>
                )}
              </div>
              <div className="optionDescription">
                Extension not required &mdash; connect your LinkedIn account
                directly.
              </div>
            </div>
          </div>
        </div>

        <div className="linkedInAccountPopupFooter">
          <Button
            id="continueLinkedInBtn"
            name="continueLinkedInBtn"
            btnText="Continue"
            className="continueLinkedInBtn"
            type="button"
            click={handleContinue}
          />
        </div>
        <div className="sendInviteDivider">
          <span>or</span>
        </div>
        <div
          className="sendInviteInsteadLink"
          onClick={() => setView("invite")}
        >
          Send an invite instead
        </div>
      </div>
    </>
  );

  const renderChooseExtensionType = () => (
    <>
      <div
        className="linkedInInviteBackLink"
        onClick={() => setView("chooseType")}
      >
        <i className="material-icons">arrow_back</i>
        Back
      </div>
      <div className="commonPopupHeader linkedInAccountPopupHeader">
        <div className="linkedinIcon">
          <LinkedinIcon />
        </div>
        <div className="linkedinHeadingWrapper">
          <div className="headingTitle">Connect LinkedIn</div>
          <div className="chooseAccountType">
            Choose the account type you want to connect
          </div>
        </div>
      </div>
      <div className="commonPopupBody linkedInAccountPopupBody">
        <div className="linkedInAccountOptions">
          <div
            className={`linkedInAccountOption ${
              selectedAccountType === "standard" ? "selected" : ""
            }`}
            onClick={() => setSelectedAccountType("standard")}
          >
            <CustomRadioButton
              name="linkedInAccountType"
              id="standard"
              value=""
              checked={selectedAccountType === "standard"}
              radioOnchange={handleAccountTypeRadioChange}
            />
            <span className="optionIcon">
              <ClassicLinkedinIcon />
            </span>
            <div className="optionContent">
              <div className="optionTitle">Standard/ Premium LinkedIn</div>
              <div className="optionDescription">
                Automate profile views, requests, and messages.
              </div>
            </div>
          </div>
          <div
            className={`linkedInAccountOption ${
              selectedAccountType === "salesNavigator" ? "selected" : ""
            }`}
            onClick={() => setSelectedAccountType("salesNavigator")}
          >
            <CustomRadioButton
              name="linkedInAccountType"
              id="salesNavigator"
              value=""
              checked={selectedAccountType === "salesNavigator"}
              radioOnchange={handleAccountTypeRadioChange}
            />
            <span className="optionIcon">
              <LinkedinPremiumIcon />
            </span>
            <div className="optionContent">
              <div className="optionTitle">
                Sales Navigator{" "}
                <span className="recommended">(recommended)</span>
              </div>
              <div className="optionDescription">
                Unlock contact imports, advanced workflows, and higher
                automation limits.
              </div>
            </div>
          </div>
        </div>
        <div className="linkedInAccountPopupFooter">
          <Button
            id="continueLinkedInBtn"
            name="continueLinkedInBtn"
            btnText="Continue"
            className="continueLinkedInBtn"
            type="button"
            click={handleContinueExtensionType}
          />
        </div>
      </div>
    </>
  );

  const renderInvite = () => (
    <>
      <div
        className="linkedInInviteBackLink"
        onClick={() => setView("chooseType")}
      >
        <i className="material-icons">arrow_back</i>
        Back
      </div>
      <div className="commonPopupHeader linkedInAccountPopupHeader">
        <div className="linkedinIcon">
          <LinkedinIcon />
        </div>
        <div className="linkedinHeadingWrapper">
          <div className="headingTitle">Send invite link</div>
          <div className="chooseAccountType">
            We'll email a one-time LinkedIn connect link to this address
          </div>
        </div>
      </div>
      <div className="commonPopupBody linkedInAccountPopupBody">
        <div className="linkedInInviteEmailField">
          <label htmlFor="linkedInInviteEmail">Email address</label>
          <input
            id="linkedInInviteEmail"
            type="email"
            placeholder="teammate@company.com"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
          />
        </div>
        <div className="linkedInAccountPopupFooter">
          <Button
            id="sendLinkedInInviteBtn"
            name="sendLinkedInInviteBtn"
            btnText="Send invite"
            className={`continueLinkedInBtn${
              !inviteEmail?.trim() ? " disabled" : ""
            }${sendingInvite ? " loading" : ""}`}
            type="button"
            loader={sendingInvite}
            click={handleSendInvite}
          />
        </div>
      </div>
    </>
  );

  const renderInviteSent = () => (
    <div className="linkedInInviteSent">
      <i className="material-icons">check_circle</i>
      <div className="headingTitle">Invite sent</div>
      <div className="chooseAccountType">
        We emailed the connect link to {inviteEmail}. It'll show up as
        pending until they finish authorizing.
      </div>
      <div className="linkedInAccountPopupFooter">
        <Button
          id="doneLinkedInInviteBtn"
          name="doneLinkedInInviteBtn"
          btnText="Done"
          className="continueLinkedInBtn"
          type="button"
          click={closePopup}
        />
      </div>
    </div>
  );

  return (
    <div className="commonPopupWrapper linkedInAccountPopupWrapper">
      <div className="common-popup-mask" />
      <div className="commonPopupContainer linkedInAccountPopupContainer animated customzoomIn">
        <div className="closeIconContainer">
          <i
            className="material-icons commonPopupCloseIcon"
            onClick={closePopup}
          >
            close
          </i>
        </div>
        {view === "chooseType" && renderChooseType()}
        {view === "chooseExtensionType" && renderChooseExtensionType()}
        {view === "invite" && renderInvite()}
        {view === "inviteSent" && renderInviteSent()}
      </div>
    </div>
  );
});

export { addLinkedInAccountPopupState };
export default AddLinkedInAccountPopup;
